import { motion } from '@eversports/react-components/design-tokens/motion'
import Button from '@eversports/react-components/primitives/Button'
import ErrorBox from '@eversports/react-components/primitives/ErrorBox'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Link from '@eversports/react-components/primitives/Link'
import Processing from '@eversports/react-components/primitives/Processing'
import Text from '@eversports/react-components/primitives/Text'
import { ClientError } from '@eversports/react-components/client-error'
import { makeStyles } from '@mui/styles'

import { Localized, useIntl } from '../../localization/react'

import { PolicyVariant } from './EversportsConsentUpdateForm'

export interface EversportsConsentBlockingUpdateFormProps {
  variant: PolicyVariant
  termsUrl?: string
  privacyUrl?: string
  loading?: boolean
  onConsentAcceptance: () => void
  onConsentRejection: () => void
  error?: ClientError
  errorUrl: string
}

const useStyles = makeStyles({
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: 24,
    maxWidth: 640,
  },
})

const EversportsConsentBlockingUpdateForm = ({
  variant,
  termsUrl,
  privacyUrl,
  loading,
  onConsentAcceptance,
  onConsentRejection,
  error,
  errorUrl,
}: EversportsConsentBlockingUpdateFormProps) => {
  const classes = useStyles()
  const intl = useIntl()

  const titleId = `consent-blocking-title-${variant}` as const
  const introId = `consent-blocking-intro-${variant}` as const
  const ackExplanationId = `consent-blocking-acknowledge-explanation-${variant}` as const

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ ease: 'easeInOut' }}
    >
      <Processing isLoading={loading}>
        <Flex flexDirection="column" verticalSpacing="default" className={classes.card}>
          <Flex alignItems="center" horizontalSpacing="dense">
            <Icon icon={Icons.WARNING} color="danger" />
            <Heading is="h1" variant="normal" data-testid="consent-blocking-heading">
              <Localized id={titleId} />
            </Heading>
          </Flex>
          <Text color="text.secondary">
            <Localized id={introId} />
          </Text>
          <Flex flexDirection="column">
            {(variant === 'terms' || variant === 'both') && termsUrl && (
              <Link to={termsUrl} external blank>
                <Localized id="consent-update-link-terms" />
              </Link>
            )}
            {(variant === 'privacy' || variant === 'both') && privacyUrl && (
              <Link to={privacyUrl} external blank>
                <Localized id="consent-update-link-privacy" />
              </Link>
            )}
          </Flex>
          <Text color="text.secondary" fontWeight="bold">
            <Localized id={ackExplanationId} />
          </Text>
          <Text color="text.secondary">
            <Localized id="consent-blocking-reject-explanation" />
          </Text>
          <Link to={intl('consent-blocking-deletion-link-url')} external blank>
            <Localized id="consent-blocking-deletion-link" />
          </Link>
          {error && (
            <ErrorBox>
              <Text color="danger" size="small" marginBottom="dense">
                <Localized id="consent-form-token-invalid-error" />
              </Text>
              <Link size="small" color="secondary" to={errorUrl}>
                <Localized id="consent-form-token-invalid-error-link" />
              </Link>
            </ErrorBox>
          )}
          <Flex
            flexDirection={['column', 'row']}
            verticalSpacing="default"
            horizontalSpacing="default"
            justifyContent="flex-end"
            marginTop="default"
          >
            <Button data-testid="reject-consent" variant="danger" onClick={onConsentRejection}>
              <Localized id="consent-blocking-reject-button" />
            </Button>
            <Button data-testid="confirm-consent" variant="primary" onClick={onConsentAcceptance}>
              <Localized id="consent-update-acknowledge-button" />
            </Button>
          </Flex>
        </Flex>
      </Processing>
    </motion.div>
  )
}

export default EversportsConsentBlockingUpdateForm
